// Locale-aware formatters — all date/number/currency formatting bound to the
// ACTIVE locale via `Intl`, never hand-rolled. Spanish formats differ
// (`1.234,56` vs `1,234.56`), so any customer-facing number/date/money must
// route through here once its surface is localized.
//
// The active locale is read from the i18next singleton at call time, so a
// picker change immediately affects subsequent formatting. These helpers are
// also registered into i18next's interpolation `format` (index.ts) so catalog
// values can inline `{{amount, currency}}` / `{{value, number}}` / `{{when, date}}`.
//
// Guardrail (cross-repo brief §11.1 + CLAUDE.md): formatting follows the active
// locale. Currency defaults to USD — the portal quotes in USD; the locale only
// changes the digit grouping/decimal glyphs, not the currency.

import i18n from 'i18next';

import { DEFAULT_LOCALE } from './resolveLocale.js';

/** The BCP-47 tag `Intl` should use — the active i18next language, or default. */
export function activeLocale(): string {
  return i18n.resolvedLanguage || i18n.language || DEFAULT_LOCALE;
}

/** Format a number in the active locale (`1,234.5` en / `1.234,5` es). */
export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(activeLocale(), options).format(value);
}

/**
 * Format money in the active locale. Currency defaults to USD (the portal's
 * quoting currency); pass a different ISO code only if the value truly is in it.
 */
export function formatCurrency(
  value: number,
  currency = 'USD',
  options?: Intl.NumberFormatOptions,
): string {
  return new Intl.NumberFormat(activeLocale(), {
    style: 'currency',
    currency,
    ...options,
  }).format(value);
}

/** Format a date/time in the active locale. Accepts a `Date`, epoch ms, or ISO string. */
export function formatDate(
  value: Date | string | number,
  options: Intl.DateTimeFormatOptions = { dateStyle: 'medium' },
): string {
  const date = value instanceof Date ? value : new Date(value);
  return new Intl.DateTimeFormat(activeLocale(), options).format(date);
}

/**
 * i18next interpolation formatter. Registered as `interpolation.format` so
 * catalog strings can format inline values in the active locale:
 *   "Total: {{amount, currency}}"  ·  "{{value, number}}"  ·  "{{when, date}}"
 * Falls back to the raw value's string form for unknown format names.
 */
export function i18nFormat(value: unknown, format?: string): string {
  if (value == null) return '';
  switch (format) {
    case 'number':
      return typeof value === 'number' ? formatNumber(value) : String(value);
    case 'currency':
      return typeof value === 'number' ? formatCurrency(value) : String(value);
    case 'date':
      return value instanceof Date || typeof value === 'string' || typeof value === 'number'
        ? formatDate(value)
        : String(value);
    default:
      return String(value);
  }
}

// Sizes, rates and durations for USB / disc content (ADR-021). Capacities are
// sold in decimal units (a 4.7 GB DVD holds 4.7 billion bytes), so content is
// shown in decimal units too, to compare like for like.
const DECIMAL_UNITS: ReadonlyArray<[number, string]> = [
  [1e12, 'terabyte'],
  [1e9, 'gigabyte'],
  [1e6, 'megabyte'],
  [1e3, 'kilobyte'],
];

function scaled(bytes: number, suffix = ''): string {
  for (const [size, unit] of DECIMAL_UNITS) {
    if (bytes >= size) {
      return formatNumber(bytes / size, {
        style: 'unit',
        unit: `${unit}${suffix}`,
        maximumFractionDigits: bytes / size >= 100 ? 0 : 1,
      });
    }
  }
  return formatNumber(bytes, { style: 'unit', unit: `byte${suffix}` });
}

export function formatDecimalBytes(bytes: number): string {
  return scaled(Math.max(0, bytes));
}

export function formatByteRate(bytesPerSecond: number): string {
  return scaled(Math.max(0, bytesPerSecond), '-per-second');
}

export function formatDuration(seconds: number): string {
  if (seconds < 90) {
    return formatNumber(Math.max(1, Math.round(seconds)), {
      style: 'unit',
      unit: 'second',
      unitDisplay: 'long',
    });
  }
  if (seconds < 90 * 60) {
    return formatNumber(Math.round(seconds / 60), {
      style: 'unit',
      unit: 'minute',
      unitDisplay: 'long',
    });
  }
  return formatNumber(Math.round(seconds / 360) / 10, {
    style: 'unit',
    unit: 'hour',
    unitDisplay: 'long',
    maximumFractionDigits: 1,
  });
}
